// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCollateralAgreementChangeRequestsV2 } from '@hooks/queries/collateralv2/collateral/useCollateralAgreementChangeRequestsV2';
import { useCollateralAgreementRequestsV2 } from '@hooks/queries/collateralv2/collateral/useCollateralAgreementRequestsV2';
import { useCollateralAgreementsV2 } from '@hooks/queries/collateralv2/collateral/useCollateralAgreementsV2';
import { useGetCollateralState } from '../common/useGetCollateralState';
import { CollateralAgreementRow } from '../obligations/types';

export const useGetAgreementRows = () => {
  const collateralAgreementRequests = useCollateralAgreementRequestsV2();
  const collateralAgreementChangeRequests = useCollateralAgreementChangeRequestsV2();
  const collateralAgreements = useCollateralAgreementsV2();
  const { getCollateralState } = useGetCollateralState();

  const isLoading =
    collateralAgreementRequests.isLoading ||
    !collateralAgreementRequests.data ||
    collateralAgreements.isLoading ||
    !collateralAgreements.data;

  const requestRows: CollateralAgreementRow[] = (collateralAgreementRequests?.data || []).map(
    (r) => ({
      contractId: r.contractId,
      payload: {
        partyA: r.payload.requestor, // requestorIsPartyA: true
        partyB: r.payload.counterparty,
        operator: r.payload.operator,
        requestor: r.payload.requestor,
        counterparty: r.payload.counterparty,
        id: r.payload.id,
        terms: r.payload.terms,
        minimumTransferAmount: r.payload.minimumTransferAmount,
      },
      state: 'Pending',
    }),
  );

  const acceptedRows: CollateralAgreementRow[] = (collateralAgreements.data || []).map((r) => {
    return {
      contractId: r.contractId,
      payload: {
        partyA: r.payload.partyA,
        partyB: r.payload.partyB,
        operator: r.payload.operator,
        counterparty: r.payload.partyB,
        calculationAgent: r.payload.calculationAgent,
        requestor: r.payload.partyA,
        id: r.payload.id,
        terms: r.payload.terms,
        minimumTransferAmount: r.payload.minimumTransferAmount,
        collateralPositions: getCollateralState(r.payload)?.payload.collateralPositions,
      },
      state: 'Accepted',
    };
  });

  const changeRequestRows: CollateralAgreementRow[] = (
    collateralAgreementChangeRequests?.data || []
  ).map((r) => {
    // TODO: Idea for the future but in cases like this, if we don't find the accepted row (probably there is a desync on backend side)
    // TODO: we could display an error / invalid row for the users.
    const acceptedAgreement = acceptedRows.find(
      (ar) => ar.contractId === r.payload.collateralAgreementCid,
    );
    return {
      contractId: r.contractId,
      payload: {
        partyA: acceptedAgreement?.payload.partyA || '::',
        partyB: acceptedAgreement?.payload.partyB || '::',
        operator: r.payload.operator,
        requestor: r.payload.requestor,
        counterparty: r.payload.counterparty,
        id: r.payload.id,
        terms: r.payload.terms,
        minimumTransferAmount: acceptedAgreement?.payload.minimumTransferAmount || '',
      },
      state: 'Change Requested',
    };
  });

  return {
    rows: [...requestRows, ...changeRequestRows, ...acceptedRows],
    isLoading,
  };
};
